import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Поле, которое сохраняется само.
 *
 * Кнопки «Сохранить» в продукте нет и быть не должно (3.5.2), но и отправлять
 * мутацию на каждое нажатие клавиши незачем: у каждой правки свои часы и своё
 * место в журнале, и слово из пяти букв превратилось бы в пять записей.
 * Поэтому правка уходит через паузу после последнего нажатия — и обязательно
 * уходит при закрытии панели, даже если пауза не успела истечь.
 *
 * Черновик локальный и от записи не пересеивается: пришедшая по сети правка
 * иначе выдёргивала бы текст из-под пальцев. Чья версия победит, решает LWW по
 * часам — то есть та, что позже, и это ровно то, чего человек и ждёт.
 */

export interface Autosave {
  draft: string;
  change: (next: string) => void;
  /** Отправить немедленно: уход из поля, Enter в заголовке, закрытие панели. */
  flush: () => void;
}

export function useAutosave(
  initial: string,
  commit: (next: string) => void,
  delayMs = 600,
): Autosave {
  const [draft, setDraft] = useState(initial);
  const pending = useRef({ draft: initial, dirty: false });

  // Обработчик держится в ref: он пересоздаётся на каждый рендер, а отправка
  // при размонтировании обязана позвать свежий, не тот, что был при монтаже.
  const commitRef = useRef(commit);
  commitRef.current = commit;

  const flush = useCallback(() => {
    if (!pending.current.dirty) return;
    pending.current.dirty = false;
    commitRef.current(pending.current.draft);
  }, []);

  useEffect(() => {
    if (!pending.current.dirty) return undefined;
    const timer = setTimeout(flush, delayMs);
    return () => clearTimeout(timer);
  }, [draft, delayMs, flush]);

  // Закрыли панель раньше паузы — правка всё равно уезжает. Потерять здесь
  // набранное значит показать человеку текст, которого нигде нет.
  useEffect(() => flush, [flush]);

  /**
   * Выгрузка страницы — не размонтирование: cleanup-эффекты React при ней не
   * выполняются, и набранное с последней паузы пропадало молча, а индикатор в
   * этот момент показывал «сохранено».
   *
   * Случаев три, и все бытовые: закрыли вкладку, свернули приложение на
   * телефоне (там вкладку выгружают без предупреждения) и перезагрузка,
   * которую делает сам обновившийся service worker — он приходит через
   * секунды после выкладки, ровно когда человек открыл карточку и печатает.
   *
   * `pagehide` ловит выгрузку и перезагрузку, `visibilitychange` — уход в фон
   * на телефоне, где до `pagehide` дело может и не дойти.
   */
  useEffect(() => {
    function onHidden(): void {
      flush();
    }

    function onVisibility(): void {
      if (globalThis.document?.visibilityState === 'hidden') flush();
    }

    // `visibilitychange` приходит на документ, а не на окно, и всплывать не
    // обязан — слушать его на `globalThis` значит не услышать вовсе.
    const page = globalThis.document;

    globalThis.addEventListener?.('pagehide', onHidden);
    page?.addEventListener('visibilitychange', onVisibility);

    return () => {
      globalThis.removeEventListener?.('pagehide', onHidden);
      page?.removeEventListener('visibilitychange', onVisibility);
    };
  }, [flush]);

  const change = useCallback((next: string) => {
    pending.current = { draft: next, dirty: true };
    setDraft(next);
  }, []);

  return { draft, change, flush };
}
